<template>
  <v-navigation-drawer
    :model-value="modelValue"
    expand-on-hover
    :permanent="permanent"
    app
    width="250"
    :class="isMobile && modelValue && 'position-fixed top-0 left-0 rounded-0 z-index-3000 w-100 h-100 bg-secondary-300'
    "
    class="pl-4"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-toolbar flat color="transparent" :class="{ 'mt-4 pl-4': isMobile && modelValue }">
      <NuxtImg v-if="isMobile" src="/icons/leadbook-white.svg" width="90" height="24" />
      <NuxtImg v-else src="/icons/leadbook-black.svg" :width="200" :height="54" class="h-100 pt-5" />

      <v-spacer />

      <v-btn
        v-if="isMobile && modelValue"
        variant="text"
        :class="{
          'text-white': isMobile && modelValue,
        }"
        @click="$emit('update:modelValue', false)"
      >
        <icon
          name="ri:close-large-fill"
          :class="{
            'text-white': isMobile && modelValue,
          }"
        />
      </v-btn>
    </v-toolbar>

    <v-list
      density="compact"
      nav
      :class="{ 'text-white': isMobile && modelValue }"
    >
      <v-list-item
        v-for="item in items"
        :id="`sidebar-${item.value}`"
        :key="item.value"
        :value="item.value"
        :active="item.value === selected"
        :disabled="item.disabled"
        :class="[
          'font-weight-medium text-body-1 py-4 mt-3 list',
          item.value === selected
            ? isMobile && modelValue ? 'text-secondary-600' : 'text-secondary'
            : 'text-neutral-600',
        ]"
        @click="() => navigate(item)"
      >
        <template #prepend>
          <v-badge v-if="item.value ==='notification' && notificationStore.unreadNotificationsCount > 0" location="top left" color="red" :content="notificationStore.unreadNotificationsCount">
            <icon :name="item.icon" class="mr-2" :class="{ 'text-white': isMobile && modelValue }" />
          </v-badge>
          <icon v-else :name="item.icon" class="mr-2" :class="{ 'text-white': isMobile && modelValue }" />
        </template>
        <v-list-item-title :class="{ 'text-white': isMobile && modelValue }">
          {{ $t(item.title) }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
    <template #append>
      <v-list
        density="compact"
        nav
        :class="{ 'text-white': isMobile && modelValue }"
      >
        <v-list-item
          v-for="item in actions"
          :id="`sidebar-${item.value}`"
          :key="item.value"
          :value="item.value"
          :active="item.value === selected"
          :disabled="item.disabled"
          :class="[
            'font-weight-medium text-body-1 py-4 mt-3 list',
            item.value === selected
              ? isMobile && modelValue ? 'text-secondary-600' : 'text-secondary'
              : 'text-neutral-600',
          ]"
          @click="clickActionHandler(item)"
        >
          <template #prepend>
            <icon :name="item.icon" class="mr-2" :class="{ 'text-white': isMobile && modelValue }" />
          </template>
          <v-list-item-title :class="{ 'text-white': isMobile && modelValue }">
            {{ $t(item.title) }}
          </v-list-item-title>
        </v-list-item>
        <UserBadge :is-mobile="isMobile" />
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import type { SidebarItem } from '@/types/sidebar';

defineProps<{
  modelValue: boolean;
  selected: string;
  items: SidebarItem[];
  actions: SidebarItem[];
  permanent?: boolean;
  isMobile: boolean;
}>();

const emit = defineEmits(['update:modelValue', 'update:selected', 'click:action']);
const router = useRouter();
const authStore = useAuthStore();

const notificationStore = useNotificationsStore();
const { data: unreadNotificationCountData, isSuccess } = useNotificationsGetUnreadNotificationsCount({
  query: {
    refetchInterval: 10000,
    enabled: !authStore.isViewer,
  },
});

watchEffect(() => {
  if (isSuccess.value && unreadNotificationCountData.value !== undefined) {
    notificationStore.setUnreadNotificationCount(unreadNotificationCountData.value);
  }
});

function navigate(item: SidebarItem) {
  emit('update:selected', item.value);
  if (item.to) router.push(item.to);
}

function clickActionHandler(item: SidebarItem) {
  if (item.to) {
    navigate(item);
  } else {
    emit('click:action', item.value);
  }
}
</script>

<style lang="scss" scoped>
.v-navigation-drawer.z-index-3000 {
  z-index: 3000 !important;
}

.list {
  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.v-list-item__spacer) {
     display: none;
  }
}
</style>
